import * as React from 'react';
import { computed, action } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as classNames from 'classnames';
import { OrderItem } from '.';
import { IOrderItemsGroup, IOrderItem } from '../../../../common/interfaces/order';
import { MakeModel } from '../../../../common/helpers/makeModel';
import { ICartItem } from '../../../../common/interfaces/search';
import { unformat } from 'accounting';
import { IPickerOption, Picker } from '../forms';
import { IAppOnlyStore } from '../../stores';
import { PortalSearchBarField } from '../layout';

// Styles
const s = require('./OrderItemsGroups.css');

// Interfaces
interface ISelectedYears extends IPickerOption {
    key: string;
    value: number;
    title: string;
}

// Props
interface IOrderItemsGroupsProps extends IAppOnlyStore {
    className?: string;
    items: ICartItem[];
    requireYearsSelection?: boolean;
    selectedYears?: { [groupConcat: string]: { vin: string | null; year: number | null } };
    onSelectedYearChange?: (groupConcat: string, itemIds: number[], selectedYear: number | null, vin: string | null) => void;
}

// Component
@inject('store')
@observer
export class OrderItemsGroups extends React.Component<IOrderItemsGroupsProps, {}> {
    static get defaultProps(): IOrderItemsGroupsProps {
        return {
            className: null,
            items: [],
            requireYearsSelection: false,
            selectedYears: {},
            onSelectedYearChange: null
        };
    }

    @computed
    private get itemsGroupedByCarModelMakeYears(): IOrderItemsGroup[] {
        const groups: IOrderItemsGroup[] = [];

        if (this.props.items.length > 0) {
            this.props.items.map((item) => {
                const concat = MakeModel.concatFromYearToYearMakeModel(item.fromYear, item.toYear, item.make, item.model);

                let groupExists: number = -1;
                groups.some((group, groupIndex) => {
                    if (group.concat === concat) {
                        groupExists = groupIndex;
                        return true;
                    }
                });

                const selectedYearGroup = this.props.selectedYears[concat] || {
                    year: null,
                    vin: null
                };

                const itemMapped: IOrderItem = {
                    make: item.make,
                    model: item.model,
                    fromYear: item.fromYear,
                    toYear: item.toYear,
                    image: item.imageThumb || item.image || null,
                    id: item.id,
                    itemcode: item.itemCode,
                    customerItemcode: item.custItemCode,
                    itemname: item.itemName,
                    oem: item.oem,
                    partsLink: item.Partslink,
                    dpi: item.dpi,
                    certification: item.certification,
                    quantity: item.quantity,
                    price: unformat(item.price) || unformat(item.listPrice) || 0
                };

                if (groupExists !== -1) {
                    groups[groupExists].items.push(itemMapped);
                    groups[groupExists].selectedYear = this.props.requireYearsSelection
                        ? typeof selectedYearGroup.year !== 'undefined'
                            ? selectedYearGroup.year
                            : null
                        : null;
                    groups[groupExists].vin = this.props.requireYearsSelection
                        ? typeof selectedYearGroup.vin !== 'undefined'
                            ? selectedYearGroup.vin
                            : null
                        : null;
                } else {
                    groups.push({
                        concat: concat,
                        fromYear: item.fromYear,
                        toYear: item.toYear,
                        make: item.make,
                        model: item.model,
                        items: [itemMapped],
                        selectedYear: this.props.requireYearsSelection
                            ? typeof selectedYearGroup.year !== 'undefined'
                                ? selectedYearGroup.year
                                : null
                            : null,
                        vin: this.props.requireYearsSelection
                            ? typeof selectedYearGroup.vin !== 'undefined'
                                ? selectedYearGroup.vin
                                : null
                            : null
                    });
                }
            });
        }

        return groups;
    }

    @action
    private handleSelectYearFieldFocus = (field: string) => {
        this.props.store.checkout.cardItemsShowSelectYear.set(field, true);
    };

    @action
    private handleSelectYearFieldBlur = (field: string) => {
        this.props.store.checkout.cardItemsShowSelectYear.delete(field);
    };

    @action
    private handleInputVINFieldBlur = (itemIds: number[], vin: string = '') => {
        this.props.store.checkout.cardItemsHasCorrectVIN.set(itemIds[0], !vin || !vin.length ? null : this.checkValidVIN(vin));
    };

    private checkValidVIN = (vin: string) => {
        const pattern = /\b(?=[^ioq])(?=.{17})[a-zA-Z0-9]{17}\b/g;
        if (!vin) {
            return false;
        }
        const matches = vin.match(pattern);
        return matches ? matches.length > 0 && matches[0].length === vin.length : false;
    };

    render() {
        return (
            <div className={classNames(this.props.className)}>
                {this.itemsGroupedByCarModelMakeYears.map((group) => {
                    const yearsSpan = group.toYear - group.fromYear;
                    const years = [];
                    for (let i = 0; i <= yearsSpan; i++) {
                        years.push(group.fromYear + i);
                    }

                    const selectedYears: ISelectedYears[] = years.map((year) => ({
                        key: year.toString(),
                        title: year.toString(),
                        value: year
                    }));

                    const selectedYearGroup = this.props.selectedYears[group.concat] || {
                        year: null,
                        vin: null
                    };

                    const currentSelectedYear = selectedYearGroup.year;
                    const currentVin = selectedYearGroup.vin;

                    return (
                        <div className={s.group} key={group.concat}>
                            <div className={s.header}>
                                <h4 className={s.makeModelYear}>{group.concat}</h4>

                                {this.props.requireYearsSelection && this.props.onSelectedYearChange && (
                                    <div className={s.selectedYears}>
                                        <Picker
                                            show={this.props.store.checkout.cardItemsShowSelectYear.get(group.concat)}
                                            onValueSelectedChange={(newValue) => (e) => {
                                                this.handleSelectedYearChange({
                                                    groupConcat: group.concat,
                                                    itemIds: group.items.map((item) => item.id),
                                                    year: newValue,
                                                    vin: currentVin ? currentVin : ''
                                                })(e);

                                                this.handleInputVINFieldBlur(
                                                    group.items.map((item) => item.id),
                                                    currentVin ? currentVin : ''
                                                );

                                                this.handleSelectYearFieldBlur(group.concat);
                                            }}
                                            valueSelected={currentSelectedYear}
                                            options={[
                                                {
                                                    name: 'Production year',
                                                    options: [
                                                        ...(currentSelectedYear === null
                                                            ? [
                                                                  {
                                                                      key: 'none',
                                                                      value: null,
                                                                      title: 'Select the year'
                                                                  }
                                                              ]
                                                            : []),
                                                        ...selectedYears
                                                    ]
                                                }
                                            ]}
                                            field={{
                                                above: true,
                                                label: currentSelectedYear ? 'Production year' : 'Select production year',
                                                isImportant: !Boolean(currentSelectedYear),
                                                isCorrect: Boolean(currentSelectedYear),
                                                onFocus: this.handleSelectYearFieldFocus.bind(this, group.concat),
                                                onBlur: this.handleSelectYearFieldBlur.bind(this, group.concat),
                                                value: currentSelectedYear ? currentSelectedYear.toString() : ''
                                            }}
                                        />

                                        <PortalSearchBarField
                                            className={classNames(s.searchField)}
                                            value={currentVin ? currentVin : ''}
                                            label={'Provide VIN'}
                                            field="input"
                                            lightness={0}
                                            isImportant={!this.checkValidVIN(currentVin)}
                                            isCorrect={this.checkValidVIN(currentVin)}
                                            onChange={(field, value, e) => {
                                                this.handleSelectedYearChange({
                                                    groupConcat: group.concat,
                                                    itemIds: group.items.map((item) => item.id),
                                                    year: currentSelectedYear,
                                                    vin: value
                                                })(e);
                                            }}
                                            onBlur={(field, e) => {
                                                this.handleInputVINFieldBlur(
                                                    group.items.map((item) => item.id),
                                                    currentVin
                                                );
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className={s.items}>
                                {group.items.map((item) => (
                                    <OrderItem key={item.id} {...item} />
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    @action
    private handleSelectedYearChange =
        (props: { groupConcat: string; itemIds: number[]; year: number; vin: string }) =>
        (e: React.MouseEvent<HTMLButtonElement> | React.ChangeEvent<HTMLInputElement>) => {
            const { groupConcat, itemIds, year, vin } = props;

            if (this.props.onSelectedYearChange) {
                this.props.onSelectedYearChange(groupConcat, itemIds, year, vin);
                this.props.store.checkout.cardItemsShowSelectYear.set(groupConcat, false);
            }
        };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/components/item/OrderItemsGroups.tsx